/* eslint-disable jsdoc/require-param-type, jsdoc/require-returns-type */
import { get, writable, type Writable } from 'svelte/store';
import {
	ACTIVE_EXPERIMENTS,
	EXPERIMENT_ATTRIBUTION_TYPES,
} from '$lib/services/experiments/client/constants';
import type { App } from '$lib/stores/app/types';

export type ExperimentAssignment = App.Experiment;

export const experiments: Writable<ExperimentAssignment[]> = writable([]);

/**
 * @returns Current session assignments.
 */
export function getExperiments(): ExperimentAssignment[] {
	return get(experiments);
}

/**
 * @param assignments - Session assignments.
 */
export function setExperiments(assignments: ExperimentAssignment[]): void {
	experiments.set(assignments);
}

/**
 * Upserts session assignments by key.
 * @param patches - Partial assignments to merge.
 */
export function upsertExperiments(patches: unknown): void {
	if (!Array.isArray(patches) || !patches.length) return;

	const nextExperiments = [...getExperiments()];
	for (const patch of patches as Array<Partial<ExperimentAssignment>>) {
		if (!patch?.key) continue;
		const entry: ExperimentAssignment = {
			assigned: patch.assigned ?? true,
			experiment_id: patch.experiment_id ?? null,
			key: patch.key,
			seen: patch.seen ?? false,
			variant: patch.variant ?? '',
		};
		const index = nextExperiments.findIndex((experiment) => experiment.key === entry.key);
		if (index >= 0) nextExperiments[index] = { ...nextExperiments[index], ...entry };
		else nextExperiments.push(entry);
	}
	setExperiments(nextExperiments);
}

/**
 * Clears every assignment from the current JavaScript session.
 */
export function resetExperiments(): void {
	experiments.set([]);
}

/**
 * Clears assignments whose definitions are attributed to the current master account.
 * Device-attributed assignments remain stable when users change on the same device.
 */
export function resetMasterExperiments(): void {
	const masterExperimentKeys = new Set(
		Object.values(ACTIVE_EXPERIMENTS)
			.filter((experiment) => experiment.attributionType === EXPERIMENT_ATTRIBUTION_TYPES.MASTER)
			.map((experiment) => experiment.experimentKey)
	);
	experiments.update((assignments) =>
		assignments.filter((assignment) => !masterExperimentKeys.has(assignment.key))
	);
}
